/**
 * Bloco de endereço da paleta do Editor (ADR-0071).
 *
 * O endereço do lead não é um campo só: são sete destinos (`cep`, `logradouro`,
 * `numero`, `complemento`, `bairro`, `cidade`, `estado`). Montar isso à mão
 * custava sete idas à paleta e sete vezes escolher o destino certo no painel —
 * e bastava esquecer o CEP para o preenchimento automático não ligar
 * (`podeBuscarCep` exige o CEP mais um destino que a busca preenche).
 *
 * Aqui fica só O QUE o bloco tem. Quem insere é o Editor; quem preenche pelo
 * CEP continua sendo o `preenchimentoCep.js`, que reconhece os campos pelo
 * destino — então preview e página `/f/slug` se comportam igual, sem tipo de
 * campo novo no backend.
 */
import { novoCampo } from "./camposHelpers";

/**
 * Os campos do bloco, na ordem em que entram no formulário. O CEP vem primeiro
 * porque é ele que preenche os outros (decisão do dono, 15/09). Número e
 * complemento a busca não sabe: quem responde digita.
 */
export const CAMPOS_DO_BLOCO_ENDERECO = [
  {
    destino: "cep",
    tipo: "TEXTO",
    label: "CEP",
    placeholder: "00000-000",
    ajuda: "Digite o CEP e o endereço é preenchido sozinho.",
    largura: "HALF",
  },
  {
    destino: "logradouro",
    tipo: "TEXTO",
    label: "Rua / Avenida",
    placeholder: "",
    ajuda: "",
    largura: "FULL",
  },
  {
    destino: "numero",
    tipo: "TEXTO",
    label: "Número",
    placeholder: "",
    ajuda: "",
    largura: "HALF",
  },
  {
    destino: "complemento",
    tipo: "TEXTO",
    label: "Complemento",
    placeholder: "Apartamento, bloco, referência",
    ajuda: "",
    largura: "HALF",
  },
  {
    destino: "bairro",
    tipo: "TEXTO",
    label: "Bairro",
    placeholder: "",
    ajuda: "",
    largura: "HALF",
  },
  {
    destino: "cidade",
    tipo: "TEXTO",
    label: "Cidade",
    placeholder: "",
    ajuda: "",
    largura: "HALF",
  },
  {
    destino: "estado",
    tipo: "TEXTO",
    label: "Estado (UF)",
    placeholder: "Selecione...",
    ajuda: "",
    largura: "HALF",
  },
];

/** Rótulo de cada destino do bloco, para as mensagens da tela. */
export const LABEL_DO_DESTINO_DE_ENDERECO = CAMPOS_DO_BLOCO_ENDERECO.reduce(
  (acc, campo) => {
    acc[campo.destino] = campo.label;
    return acc;
  },
  {}
);

/**
 * Campos do bloco que ainda cabem no formulário, já prontos para entrar na
 * lista. Destino que outro campo já usa fica de fora: o servidor aceita um
 * campo por destino, e duplicar faria o "Salvar campos" recusar o lote inteiro.
 *
 * @param {number} ordemInicial quantos campos o formulário já tem
 * @param {Object} destinosUsados `{destino: "Rótulo do campo que usa"}`
 * @returns {{campos: Array, jaNoFormulario: string[]}} campos novos e os
 *   rótulos dos destinos que foram pulados
 */
export function blocoDeEndereco(ordemInicial = 0, destinosUsados = {}) {
  const campos = [];
  const jaNoFormulario = [];

  CAMPOS_DO_BLOCO_ENDERECO.forEach((modelo) => {
    if (destinosUsados[modelo.destino]) {
      jaNoFormulario.push(modelo.label);
      return;
    }
    const campo = novoCampo(modelo.tipo, ordemInicial + campos.length);
    campos.push({
      ...campo,
      label: modelo.label,
      placeholder: modelo.placeholder,
      ajuda: modelo.ajuda,
      largura: modelo.largura,
      mapeia_para: modelo.destino,
    });
  });

  return { campos, jaNoFormulario };
}

/** Frase do toast depois de inserir o bloco. */
export function avisoDoBlocoDeEndereco({ campos, jaNoFormulario }) {
  if (!campos.length) {
    return "O formulário já tem todos os campos de endereço.";
  }
  const quantos =
    campos.length === 1
      ? "1 campo de endereço adicionado"
      : `${campos.length} campos de endereço adicionados`;
  if (!jaNoFormulario.length) return `${quantos}.`;
  return `${quantos}. Já estavam no formulário: ${jaNoFormulario.join(", ")}.`;
}
